import { useQueryClient } from "@tanstack/react-query"
import {
  type LinkProps,
  type MakeRouteMatchUnion,
  useRouter,
} from "@tanstack/react-router"
import { Hub } from "aws-amplify/utils"
import { useEffect } from "react"

import { FileRouteTypes } from "@/routeTree.gen"
import { SHARED_BASE_KEY, THEME_BASE_KEY } from "@/utilities/hooks/queries/keys"
import { getQrLookupIDFromRoute } from "@/utilities/hooks/useTheme"
import { log } from "@/utilities/logging"

/**
 * Where a sign-out lands, first match wins, falling back to home. In-lane gets
 * its own entrances: `/welcome`'s gated button opens the *register* flow, so a
 * driver sent there can only get back by rescanning the PAM. `$sessionID` is a
 * descendant of `$pamID`, so it is tested first.
 */
const SIGNED_OUT_DESTINATIONS: readonly {
  routeId: MakeRouteMatchUnion["routeId"]
  to: FileRouteTypes["to"]
}[] = [
  {
    routeId: "/qr/$qrLookupID/gated/$pamID/$sessionID",
    to: "/qr/$qrLookupID/gated/$pamID/$sessionID/cart",
  },
  {
    routeId: "/qr/$qrLookupID/gated/$pamID",
    to: "/qr/$qrLookupID/gated/$pamID/look-up",
  },
  { routeId: "/qr/$qrLookupID", to: "/qr/$qrLookupID/welcome" },
]

function getSignedOutDestination(
  matches: MakeRouteMatchUnion[]
): Pick<LinkProps, "to" | "params"> {
  for (const { routeId, to } of SIGNED_OUT_DESTINATIONS) {
    const match = matches.find((candidate) => candidate.routeId === routeId)
    if (match) return { to, params: match.params }
  }

  return { to: "/" }
}

/**
 * Keeps the router and the query cache in step with Amplify's auth session.
 * Mount it once, at the root
 */
export function useAuthHubListener() {
  const queryClient = useQueryClient()
  const router = useRouter()

  useEffect(() => {
    const clearNonThemeData = () =>
      queryClient.resetQueries({
        predicate: (query) =>
          !(
            query.queryKey[0] === SHARED_BASE_KEY &&
            query.queryKey[1] === THEME_BASE_KEY
          ),
      })

    /*
     * Only clear the cache. The auth form navigates itself, so invalidating the
     * router would put a second load in flight beside the form's. Guards that
     * clear the cache mid-load cancel the other load's queries, and the
     * CancelledError lands in its error boundary for the frame before the surviving
     * load replaces it.
     */
    const handleSignedIn = () => clearNonThemeData()

    const handleSignedOut = async () => {
      const currentMatch = router.state.matches[router.state.matches.length - 1]

      /**
       * signedOut also fires when the query layer clears an already dead
       * session, where nobody had a session to leave. isAuthenticated is a
       * pre-signOut read, so it tells the two apart; guards handle the
       * latter, offering /sign-in with a way back.
       */
      if (!currentMatch?.context.isAuthenticated) {
        log({
          level: "warn",
          flow: "misc",
          scope: "auth.staleSession",
          message:
            "Received signedOut while already unauthenticated, invalidating without navigating",
          meta: { from: router.state.location.href },
        })

        await router.invalidate()
        await clearNonThemeData()
        return
      }

      const signedOutQrLookupID = getQrLookupIDFromRoute({
        params: currentMatch.params,
        search: currentMatch.search,
      })
      const destination = getSignedOutDestination(router.state.matches)
      const destinationLocation = router.buildLocation(destination)

      /**
       * Navigating skips the departed route's guards, but is a no-op when
       * already there, which would strand beforeLoad on stale context.
       */
      const alreadyOnDestination =
        router.state.location.pathname === destinationLocation.pathname

      log({
        level: "info",
        flow: "misc",
        scope: "auth.signedOut",
        message: alreadyOnDestination
          ? "Signed out, already on the entrance, reloading it as a guest"
          : "Signed out, redirecting to the entrance",
        meta: {
          from: router.state.location.href,
          to: destinationLocation.href,
          qrLookupID: signedOutQrLookupID,
        },
      })

      /*
       * The cache is cleared only once the page the user is leaving is gone,
       * so the departing route never re-renders against an emptied cache.
       */
      if (alreadyOnDestination) {
        await router.invalidate()
      } else {
        await router.navigate({ ...destination, replace: true })
      }

      await clearNonThemeData()
    }

    /*
     * Events can land while an earlier handler is still awaiting. Chaining
     * them onto one queue keeps a late sign-out from clearing the cache
     * underneath the sign-in that followed it.
     */
    let queue = Promise.resolve()

    const enqueue = (handler: () => Promise<void>) => {
      queue = queue.then(handler).catch((error: unknown) => {
        log({
          level: "error",
          flow: "misc",
          scope: "auth.hubHandler",
          message: "Failed to handle an auth session change",
          meta: { error },
        })
      })
    }

    const hubListener = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          enqueue(handleSignedIn)
          break
        case "signedOut":
          enqueue(handleSignedOut)
          break
      }
    })

    return () => {
      hubListener()
    }
  }, [queryClient, router])
}
